import React, { useState } from "react";
import { PlayArrowOutlined } from "@mui/icons-material";
import { Alert, Box, Button, CircularProgress } from "@mui/material";
import useApi from "../../hooks/useApi";
import {
  previewWorkflowWebservice,
  WorkflowPreviewOutcome,
} from "../../services/workflowDefinitionService";
import { WorkflowWebservicePreviewRequestDto } from "../../types/workflowDefinitionDto";
import { workflowMethodSendsBody, WorkflowWebServiceConfig } from "./workflowBuilderData";

interface WorkflowWebServicePreviewProps {
  config: WorkflowWebServiceConfig;
}

const ON_FAILURE_WORDS: Record<WorkflowWebServiceConfig["onFailure"], string> = {
  continue: "continue",
  cancel: "cancel",
};

/** The canvas config as Epic 80423's dry-run request — the same shape the runtime replays. */
const toPreviewRequest = (config: WorkflowWebServiceConfig): WorkflowWebservicePreviewRequestDto => ({
  method: config.method,
  url: config.url.trim(),
  params: config.params.filter((pair) => pair.key.trim()).map(({ key, value }) => ({ key, value })),
  headers: config.headers.filter((pair) => pair.key.trim()).map(({ key, value }) => ({ key, value })),
  responseCaptures: config.captures
    .filter((capture) => capture.variable.trim() && capture.responseField.trim())
    .map((capture) => ({ var: capture.variable, path: capture.responseField })),
  onFailure: config.onFailure,
  bodyTemplate: workflowMethodSendsBody(config.method) && config.body?.trim() ? config.body : null,
});

const RESPONSE_PREVIEW_LIMIT = 2000;

/** A response body for display: pretty JSON (or the raw text), cut short so a large payload can't
 *  swamp the dialog. */
const formatResponse = (body: unknown) => {
  if (body === null || body === undefined || body === "") return null;
  const text = typeof body === "string" ? body : JSON.stringify(body, null, 2);
  return text.length > RESPONSE_PREVIEW_LIMIT ? `${text.slice(0, RESPONSE_PREVIEW_LIMIT)}\n…` : text;
};

const formatValue = (value: unknown) => (typeof value === "string" ? value : JSON.stringify(value));

const RATE_LIMITED = 429;
export const PREVIEW_RATE_LIMIT_MESSAGE = "Too many previews. Try again in a minute.";

const formatCaptured = (captured: Record<string, unknown>) => {
  const names = Object.keys(captured);
  return names.length > 0 ? names.join(", ") : "nothing";
};

/**
 * The Web service canvas's Preview (Epic 80423 dry run). It calls the configured endpoint once and
 * reports what came back — it never advances a real case. A remote failure is reported with what
 * the configured on-failure behavior would do at run time.
 */
const WorkflowWebServicePreview: React.FC<WorkflowWebServicePreviewProps> = ({ config }) => {
  const { executeApi } = useApi();
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState<{ requestKey: string; outcome: WorkflowPreviewOutcome } | null>(
    null,
  );

  const request = toPreviewRequest(config);
  const requestKey = JSON.stringify(request);
  // A result describes the call it ran; once the config would send a different call it is stale.
  const outcome = lastRun?.requestKey === requestKey ? lastRun.outcome : null;

  const handlePreview = async () => {
    setRunning(true);
    // null: a failure useApi has already alerted (network, 5xx from the API itself).
    const next = await executeApi(previewWorkflowWebservice, request);
    setRunning(false);
    setLastRun(next ? { requestKey, outcome: next } : null);
  };

  const handlePreviewClick = () => {
    void handlePreview();
  };

  const renderOutcome = () => {
    if (!outcome) return null;
    if (outcome.kind === "refused") {
      // The rate limit gets the spec's own wording; a config refusal shows the API's reason.
      return (
        <Alert severity={outcome.status === RATE_LIMITED ? "warning" : "error"}>
          {outcome.status === RATE_LIMITED ? PREVIEW_RATE_LIMIT_MESSAGE : outcome.message}
        </Alert>
      );
    }
    const { data } = outcome;
    // httpStatus 0: the call never completed (timeout, DNS, TLS); `error` says which.
    const status = data.httpStatus || data.error || "no response";
    return (
      <>
        {data.ok ? (
          <Alert severity="success">
            Preview succeeded — captured: {formatCaptured(data.capturedPreview)}
            {data.unknownChips && data.unknownChips.length > 0 && (
              <span className="workflow-canvas-dialog__preview-note">
                No sample value for: {data.unknownChips.join(", ")}
              </span>
            )}
          </Alert>
        ) : (
          <Alert severity="warning">
            Preview failed ({status}). On failure this stage will {ON_FAILURE_WORDS[config.onFailure]}.
            {data.httpStatus && data.error ? (
              <span className="workflow-canvas-dialog__preview-note">{data.error}</span>
            ) : null}
          </Alert>
        )}
        {renderDetails(data.capturedPreview, data.responseBody)}
      </>
    );
  };

  // The capture mapping (variable → the value it would get) and the response it was read from.
  const renderDetails = (captured: Record<string, unknown>, responseBody: unknown) => {
    const entries = Object.entries(captured ?? {});
    const response = formatResponse(responseBody);
    if (entries.length === 0 && !response) return null;
    return (
      <Box className="workflow-canvas-dialog__preview-details">
        {entries.length > 0 && (
          <dl className="workflow-canvas-dialog__preview-captures" aria-label="Captured values">
            {entries.map(([name, value]) => (
              <React.Fragment key={name}>
                <dt>{name}</dt>
                <dd>{formatValue(value)}</dd>
              </React.Fragment>
            ))}
          </dl>
        )}
        {response && (
          <pre className="workflow-canvas-dialog__preview-response" aria-label="Response">
            {response}
          </pre>
        )}
      </Box>
    );
  };

  return (
    <Box className="workflow-canvas-dialog__preview" aria-live="polite">
      <Button
        variant="outlined"
        size="small"
        startIcon={running ? <CircularProgress size={16} color="inherit" /> : <PlayArrowOutlined />}
        onClick={handlePreviewClick}
        disabled={running || !config.url.trim()}
      >
        Preview
      </Button>
      {renderOutcome()}
    </Box>
  );
};

export default WorkflowWebServicePreview;
